/**
 * The MIT License (MIT)
 *
 * Copyright (c) 2015 Jonathan Rowny
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 *
 * https://github.com/jrowny/react-absolute-grid
 *
 */
class LayoutManager {
    /* eslint-disable no-undef */
    columns;
    horizontalMargin;
    itemHeight;
    itemWidth;
    layoutWidth;
    rowHeight;
    verticalMargin;
    /* eslint-enable no-undef */

    constructor(options, width) {
        this.update(options, width);
    }

    update(options, width) {
        this.layoutWidth = width;
        this.zoom = options.zoom;
        this.itemWidth = Math.round(options.itemWidth * this.zoom);
        this.itemHeight = Math.round(options.itemHeight * this.zoom);
        this.columns = Math.floor(this.layoutWidth / this.itemWidth);
        this.horizontalMargin =
            this.columns === 1
                ? 0
                : Math.round(this.layoutWidth - this.columns * this.itemWidth) / (this.columns - 1);
        this.verticalMargin =
            options.verticalMargin === -1 ? this.horizontalMargin : options.verticalMargin;
        this.rowHeight = this.itemHeight + this.verticalMargin;
    }

    getTotalHeight(filteredTotal) {
        return Math.ceil(filteredTotal / this.columns) * this.rowHeight - this.verticalMargin;
    }

    getRow(index) {
        return Math.floor(index / this.columns);
    }

    getColumn(index) {
        return index - this.getRow(index) * this.columns;
    }

    getPosition(index) {
        const col = this.getColumn(index);
        const row = this.getRow(index);
        const margin = this.horizontalMargin;
        const width = this.itemWidth;

        return {
            x: Math.round(col * width + col * margin),
            y: Math.round((this.itemHeight + this.verticalMargin) * row),
        };
    }

    getTransform(index) {
        const position = this.getPosition(index);
        return 'translate3d(' + position.x + 'px, ' + position.y + 'px, 0)';
    }

    getStyle(index, animation) {
        const transform = this.getTransform(index);
        const style = {
            width: this.itemWidth + 'px',
            height: this.itemHeight + 'px',
            WebkitTransform: transform,
            MozTransform: transform,
            msTransform: transform,
            transform: transform,
            position: 'absolute',
            boxSizing: 'border-box',
            display: 'block',
        };

        if (animation) {
            style.WebkitTransition = '-webkit-' + animation;
            style.MozTransition = '-moz-' + animation;
            style.msTransition = 'ms-' + animation;
            style.transition = animation;
        }

        return style;
    }
}

export default LayoutManager;
